"use client";

import React, { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/firebaseConfig';
import useBackgroundMusic from '@/hooks/useBackgroundMusic';
import useTtsPlayer from '@/hooks/useTtsPlayer';
import {
  DYNAMIC_TTS_VARIANTS,
  STATUS_LINE_VARIANTS,
  TTS_FALLBACK_LINES,
  TTS_SINGLE_LINES
} from '@/utils/ttsStaticLines';
import './audio.css';

const DEV_UID = 'jXJMXuo3x6d1pm1X2Ym7zWtaTPR2';

const randomFromArray = (arr) => arr[Math.floor(Math.random() * arr.length)];

const randomFromVariants = (variants) => {
  const keys = Object.keys(variants || {});
  if (keys.length === 0) return '';
  const key = randomFromArray(keys);
  const pool = variants[key];
  if (Array.isArray(pool) && pool.length > 0) return randomFromArray(pool);
  if (typeof pool === 'string') return pool;
  return '';
};

const randomFromSingles = () => {
  const values = Object.values(TTS_SINGLE_LINES || {});
  return values.length ? randomFromArray(values) : '';
};

const randomFromFallbacks = () => {
  const values = Object.values(TTS_FALLBACK_LINES || {});
  return values.length ? randomFromArray(values) : '';
};

export default function AudioLabPage() {
  const [userId, setUserId] = useState(null);
  const [ttsText, setTtsText] = useState('Welcome to the audio lab.');
  const [voiceId, setVoiceId] = useState('Aversa');
  const [musicVolume, setMusicVolume] = useState(1);
  const [ttsVolume, setTtsVolume] = useState(1);
  const [sfxVolume, setSfxVolume] = useState(1);
  const [trackName, setTrackName] = useState('default');
  const [playbackRate, setPlaybackRate] = useState(1);
  const [preserveTempo, setPreserveTempo] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);

  const audioSettings = useMemo(() => ({
    master: true,
    music: musicEnabled,
    sfx: true,
    host: voiceId
  }), [musicEnabled, voiceId]);

  const { setActiveTrack, preloadTrack } = useBackgroundMusic(audioSettings);
  const {
    speak,
    playWhoosh,
    playVwomp,
    playSmallWhoosh,
    playMiniWhoosh,
    playLoserSound,
    playScoreRevealBeep,
    playScoreTransferBeep,
    playConfirmBeep,
    playErrorBeep,
    playCountdownBeep,
    playKeypressBeep
  } = useTtsPlayer({ defaultVoiceId: voiceId, ttsVolume, sfxVolume });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    preloadTrack('finale');
  }, [preloadTrack]);

  const isDev = userId === DEV_UID;

  const levelsExport = JSON.stringify({
    music: Number(musicVolume),
    tts: Number(ttsVolume),
    sfx: Number(sfxVolume),
    playbackRate: Number(playbackRate)
  }, null, 2);

  if (!isDev) {
    return (
      <div className="audio-lab">
        <div className="audio-lab__panel audio-lab__panel--gate">
          <p>Audio Lab is restricted to dev accounts.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-lab">
      <header className="audio-lab__header">
        <div>
          <p className="audio-lab__eyebrow">Acroverse Dev</p>
          <h1>Audio Lab</h1>
        </div>
        <div className="audio-lab__header-actions">
          <button
            type="button"
            className="lab-button lab-button--ghost"
            onClick={() => {
              navigator.clipboard.writeText(levelsExport).catch(() => {});
            }}
          >
            Copy Levels
          </button>
          <button
            type="button"
            className="lab-button lab-button--outline"
            onClick={() => setActiveTrack(null)}
          >
            Stop Music
          </button>
        </div>
      </header>

      <div className="audio-lab__grid">
        <section className="audio-card">
          <h2>Background Music</h2>
          <div className="audio-card__row">
            <label>
              Track
              <select value={trackName} onChange={(e) => setTrackName(e.target.value)}>
                <option value="default">Default</option>
                <option value="tension">Tension</option>
                <option value="finale">Finale</option>
              </select>
            </label>
            <label>
              Volume
              <input
                type="number"
                min="0"
                max="2"
                step="0.05"
                value={musicVolume}
                onChange={(e) => setMusicVolume(Number(e.target.value))}
              />
            </label>
          </div>
          <div className="audio-card__row">
            <label>
              Playback Rate
              <input
                type="number"
                min="0.5"
                max="1.5"
                step="0.01"
                value={playbackRate}
                onChange={(e) => setPlaybackRate(Number(e.target.value))}
              />
            </label>
            <label className="lab-toggle">
              <input
                type="checkbox"
                checked={preserveTempo}
                onChange={(e) => setPreserveTempo(e.target.checked)}
              />
              <span>Preserve Tempo</span>
            </label>
          </div>
          <div className="audio-card__row">
            <label className="lab-toggle">
              <input
                type="checkbox"
                checked={musicEnabled}
                onChange={(e) => setMusicEnabled(e.target.checked)}
              />
              <span>Music Enabled</span>
            </label>
          </div>
          <div className="audio-card__actions">
            <button
              type="button"
              className="lab-button"
              onClick={() =>
                setActiveTrack(trackName, {
                  restart: true,
                  volume: musicVolume,
                  playbackRate,
                  preserveTempo
                })
              }
            >
              Play Track
            </button>
            <button
              type="button"
              className="lab-button lab-button--ghost"
              onClick={() =>
                setActiveTrack(trackName, {
                  startAtNextLoop: true,
                  restart: true,
                  volume: musicVolume,
                  playbackRate,
                  preserveTempo
                })
              }
            >
              Queue Next Loop
            </button>
          </div>
        </section>

        <section className="audio-card">
          <h2>TTS Preview</h2>
          <div className="audio-card__row">
            <label>
              Voice
              <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                <option value="Aversa">Aversa</option>
                <option value="Ben">Ben</option>
              </select>
            </label>
            <label>
              Volume
              <input
                type="number"
                min="0"
                max="2"
                step="0.05"
                value={ttsVolume}
                onChange={(e) => setTtsVolume(Number(e.target.value))}
              />
            </label>
          </div>
          <label className="audio-card__stack">
            Text
            <textarea
              value={ttsText}
              onChange={(e) => setTtsText(e.target.value)}
              rows={4}
            />
          </label>
          <div className="audio-card__actions">
            <button
              type="button"
              className="lab-button"
              onClick={() => speak(ttsText, { volume: 1 })}
            >
              Speak Text
            </button>
            <button
              type="button"
              className="lab-button lab-button--ghost"
              onClick={() => {
                const line = randomFromVariants(STATUS_LINE_VARIANTS);
                setTtsText(line);
                speak(line, { volume: 1 });
              }}
            >
              Random Status Line
            </button>
            <button
              type="button"
              className="lab-button lab-button--ghost"
              onClick={() => {
                const line = randomFromVariants(DYNAMIC_TTS_VARIANTS);
                setTtsText(line);
                speak(line, { volume: 1 });
              }}
            >
              Random Dynamic Line
            </button>
            <button
              type="button"
              className="lab-button lab-button--ghost"
              onClick={() => {
                const line = randomFromSingles() || randomFromFallbacks();
                setTtsText(line);
                speak(line, { volume: 1 });
              }}
            >
              Random Single Line
            </button>
          </div>
        </section>

        <section className="audio-card">
          <h2>SFX Bench</h2>
          <div className="audio-card__row">
            <label>
              Volume
              <input
                type="number"
                min="0"
                max="2"
                step="0.05"
                value={sfxVolume}
                onChange={(e) => setSfxVolume(Number(e.target.value))}
              />
            </label>
            <label>
              Countdown Beep
              <input
                type="number"
                min="1"
                max="10"
                step="1"
                defaultValue={5}
                onChange={(e) => playCountdownBeep(Number(e.target.value))}
              />
            </label>
          </div>
          <div className="audio-card__actions audio-card__actions--grid">
            <button type="button" className="lab-button" onClick={() => playWhoosh()}>
              Whoosh
            </button>
            <button type="button" className="lab-button" onClick={() => playSmallWhoosh()}>
              Soft Whoosh
            </button>
            <button type="button" className="lab-button" onClick={() => playMiniWhoosh()}>
              Mini Whoosh
            </button>
            <button type="button" className="lab-button" onClick={() => playVwomp()}>
              Vwomp
            </button>
            <button type="button" className="lab-button" onClick={() => playLoserSound()}>
              Loser
            </button>
            <button type="button" className="lab-button" onClick={() => playScoreRevealBeep(1)}>
              Score Reveal
            </button>
            <button type="button" className="lab-button" onClick={() => playScoreTransferBeep(3)}>
              Score Transfer
            </button>
            <button type="button" className="lab-button" onClick={() => playConfirmBeep()}>
              Confirm
            </button>
            <button type="button" className="lab-button" onClick={() => playErrorBeep()}>
              Error
            </button>
            <button type="button" className="lab-button" onClick={() => playKeypressBeep(false)}>
              Keypress
            </button>
            <button type="button" className="lab-button" onClick={() => playKeypressBeep(true)}>
              Backspace
            </button>
          </div>
        </section>

        <section className="audio-card audio-card--wide">
          <h2>Export Snapshot</h2>
          <p className="audio-card__note">Copy these numbers into the shared audio settings when you are ready.</p>
          <textarea className="audio-card__export" value={levelsExport} readOnly rows={6} />
        </section>
      </div>
    </div>
  );
}
